<template>
  <div class="transfer-details">
    <!-- Outbound transfer -->
    <div v-if="outboundTransferInfo" class="mb-1">
      <p v-if="typeof outboundTransferInfo === 'object'" class="mb-0 caption">
        <span class="caption-strong">{{ t('reservations.outbound') }}</span> {{ outboundTransferInfo.departureDate ? formatDate(outboundTransferInfo.departureDate) + ' ' : '' }}{{ outboundTransferInfo.originLocation }}{{ !is24HourService(outboundTransferInfo.departureTime) ? ' ' + outboundTransferInfo.departureTime : '' }} → {{ outboundTransferInfo.destinationLocation }}{{ !is24HourService(outboundTransferInfo.arrivalTime) ? ' ' + outboundTransferInfo.arrivalTime : '' }}
      </p>
      <p v-else class="mb-0 caption">
        <span class="caption-strong">{{ t('reservations.outbound') }}</span> {{ outboundTransferInfo }}
      </p>
    </div>
    
    <!-- Return transfer -->
    <div v-if="returnTransferInfo">
      <p v-if="typeof returnTransferInfo === 'object'" class="mb-0 caption">
        <span class="caption-strong">{{ t('reservations.return') }}</span> {{ returnTransferInfo.departureDate ? formatDate(returnTransferInfo.departureDate) + ' ' : '' }}{{ returnTransferInfo.originLocation }}{{ !is24HourService(returnTransferInfo.departureTime) ? ' ' + returnTransferInfo.departureTime : '' }} → {{ returnTransferInfo.destinationLocation }}{{ !is24HourService(returnTransferInfo.arrivalTime) ? ' ' + returnTransferInfo.arrivalTime : '' }}
      </p>
      <p v-else class="mb-0 caption">
        <span class="caption-strong">{{ t('reservations.return') }}</span> {{ returnTransferInfo }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { defineProps, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { TransferDetails } from '@/services/BookingService.interface'

  // Initialize i18n
  const { t } = useI18n()

  // Define the props
  const props = defineProps<{
    tripInformation: any
  }>()

  // Funicular location mapping - specifically for Les Arcs destinations
  const funicularLocationMap: Record<string, string> = {
    // Les Arcs destinations by ID
    '96': 'Les Arcs 1600',
    '27': 'Les Arcs 1800', 
    '336': 'Les Arcs 1950',
    '97': 'Les Arcs 2000',
    // Common origin station
    'BOURG_SAINT_MAURICE': 'Bourg Saint Maurice',
    'BOURG SAINT MAURICE': 'Bourg Saint Maurice',
  }

  // Helper function to get display name for funicular locations
  const getDisplayName = (location: string | number, isDestination = false): string => {
    const locationStr = String(location).trim()
    
    // Only apply mapping for funicular transfers
    const details = transferDetails.value
    const isFunicular = details?.transferType === 'FUNICULAR' || 
                       (details?.outboundHours && !Array.isArray(details.outboundHours) && (details.outboundHours as any).provider === 'FUNICULAR') ||
                       (details?.inboundHours && !Array.isArray(details.inboundHours) && (details.inboundHours as any).provider === 'FUNICULAR')
    
    if (!isFunicular) {
      // For non-funicular transfers (bus/taxi), return original location
      return locationStr
    }
    
    // If this is a funicular destination and we have a destinationId, use that for mapping
    if (isDestination && details?.destinationId) {
      const destinationId = details.destinationId
      if (funicularLocationMap[destinationId]) {
        console.log('DEBUG: destinationId =', destinationId, '→', funicularLocationMap[destinationId])
        return funicularLocationMap[destinationId]
      }
    }
    
    // Otherwise use the location string for mapping (only for funicular)
    const result = funicularLocationMap[locationStr] || locationStr
    console.log('DEBUG: location =', locationStr, '→', result)
    return result
  }

  // Get the transfer details (handle array format)
  const transferDetails = computed((): TransferDetails | null => {
    if (Array.isArray(props.tripInformation) && props.tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      return props.tripInformation[0].details || props.tripInformation[0].tripInformation
    }
    return props.tripInformation
  })

  // Check if this is a taxi/funicular transfer (object format) or bus transfer (array format)
  const isTaxiTransfer = computed(() => {
    const details = transferDetails.value
    return details?.transferType === 'TAXI' || 
           details?.transferType === 'FUNICULAR' ||
           (details?.outboundHours && !Array.isArray(details.outboundHours) && (details.outboundHours as any).provider === 'FUNICULAR') ||
           (details?.inboundHours && !Array.isArray(details.inboundHours) && (details.inboundHours as any).provider === 'FUNICULAR') ||
           (details?.outboundHours && !Array.isArray(details.outboundHours))
  })


  // Get outbound transfer information
  const outboundTransferInfo = computed(() => {
    const details = transferDetails.value
    if (!details) return null

    if (isTaxiTransfer.value) {
      // Handle taxi/funicular transfers (object format) - format consistently with flights
      const outbound = details.outboundHours
      if (outbound && !Array.isArray(outbound) && 'from' in outbound && 'to' in outbound && details.origin && details.destination) {
        return {
          originLocation: getDisplayName(details.origin, false),
          destinationLocation: getDisplayName(details.destination, true),
          departureTime: outbound.from,
          arrivalTime: outbound.to,
          departureDate: (outbound as any).date || details.departureDate || (details as any).startDate || ''
        }
      }
    } else {
      // Handle bus transfers (array format)
      const selectedTransfer = getSelectedDepartureTransfer()
      if (selectedTransfer) {
        return formatBusTransferTime(selectedTransfer, false)
      }
    }
    
    return null
  })

  // Get return transfer information
  const returnTransferInfo = computed(() => {
    const details = transferDetails.value
    if (!details) return null

    if (isTaxiTransfer.value) {
      // Handle taxi/funicular transfers (object format) - format consistently with flights
      const inbound = details.inboundHours
      if (inbound && !Array.isArray(inbound) && 'from' in inbound && 'to' in inbound && details.origin && details.destination) {
        return {
          originLocation: getDisplayName(details.destination, true),
          destinationLocation: getDisplayName(details.origin, false),
          departureTime: inbound.from,
          arrivalTime: inbound.to,
          departureDate: (inbound as any).date || details.returnDate || (details as any).endDate || ''
        }
      }
    } else {
      // Handle bus transfers (array format)
      const selectedTransfer = getSelectedReturnTransfer()
      if (selectedTransfer) {
        return formatBusTransferTime(selectedTransfer, true)
      }
    }
    
    return null
  })

  // Find the selected departure transfer for bus transfers
  const getSelectedDepartureTransfer = () => {
    const details = transferDetails.value
    if (!details?.transferDepartureRef || !Array.isArray(details?.outboundHours)) {
      return null
    }
    
    return details.outboundHours.find(transfer => 
      transfer.trip_short_name === details.transferDepartureRef ||
      transfer.bus.toString() === details.transferDepartureRef
    )
  }

  // Find the selected return transfer for bus transfers
  const getSelectedReturnTransfer = () => {
    const details = transferDetails.value
    if (!details?.transferReturnRef || !Array.isArray(details?.inboundHours)) {
      return null
    }
    
    return details.inboundHours.find(transfer => 
      transfer.trip_short_name === details.transferReturnRef ||
      transfer.bus.toString() === details.transferReturnRef
    )
  }

  // Helper function to format date as DD/MM/YY
  const formatDate = (dateString: string): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)
    return `${day}/${month}/${year}`
  }

  // Helper function to check if time indicates 24/7 service
  const is24HourService = (timeString: string): boolean => {
    return timeString === '00:01' || timeString === '23:59'
  }



  // Format bus transfer time with full route information
  const formatBusTransferTime = (transfer: any, isReturn: boolean) => {
    if (!transfer) return ''
    
    // Get departure and arrival information from the transfer itself
    const departLocation = transfer.departInfo?.name || ''
    const arriveLocation = transfer.arriveeInfo?.name || ''
    const departTime = transfer.depart
    const arriveTime = transfer.arrivee
    
    // If we have location info from the transfer, use it
    if (departLocation && arriveLocation && departTime && arriveTime) {
      return {
        originLocation: departLocation,
        destinationLocation: arriveLocation,
        departureTime: departTime,
        arrivalTime: arriveTime
      }
    }
    
    // Fallback: use main transfer route info and determine direction
    const details = transferDetails.value
    if (details?.origin && details?.destination && departTime && arriveTime) {
      if (isReturn) {
        // Return journey: destination → origin
        return {
          originLocation: details.destination,
          destinationLocation: details.origin,
          departureTime: departTime,
          arrivalTime: arriveTime
        }
      } else {
        // Outbound journey: origin → destination  
        return {
          originLocation: details.origin,
          destinationLocation: details.destination,
          departureTime: departTime,
          arrivalTime: arriveTime
        }
      }
    }
    
    // Final fallback with route type - return as string for legacy cases
    if (departTime && arriveTime) {
      const routeType = transfer.route_type || transfer.route?.type || 'Bus'
      return `${routeType} • ${departTime} → ${arriveTime}`
    } else if (departTime) {
      const routeType = transfer.route_type || transfer.route?.type || 'Bus'
      return `${routeType} • Departs ${departTime}`
    }
    
    return transfer.route_type || transfer.route?.type || 'Bus'
  }
</script>

<style scoped>
  .transfer-details {
    padding: 0.25rem 0;
  }
</style> 